import { useState } from "react";
import { Star, ChevronDown, Sparkles } from "lucide-react";
import { SpotlightOrbs } from "./SpotlightOrbs";
import { NoiseTexture } from "./NoiseTexture";
import { useAnimateOnScroll } from "@/hooks/useAnimateOnScroll";
import { useScrollProgress } from "@/hooks/useScrollProgress";

interface Review {
  reviewer_name: string;
  rating: number;
  review_text: string | null;
  review_date: string | null;
  reviewer_badge: string | null;
}

interface SpotlightReviewsProps {
  reviews: Review[];
  reviewSummary?: string | null;
  totalReviewCount?: number | null;
  primaryColor?: string | null;
}

function StarRating({ rating, size = "sm" }: { rating: number; size?: "sm" | "lg" }) {
  const cls = size === "lg" ? "h-6 w-6" : "h-4 w-4";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={cls}
          fill={i <= rating ? "#FBBF24" : "transparent"}
          stroke={i <= rating ? "#FBBF24" : "rgba(255,255,255,0.2)"}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function formatReviewDate(dateStr: string): string {
  try {
    // Zillow dates come as "2026-01-12T13:41:00" (no timezone)
    // Parse as local and format as readable date
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export function SpotlightReviews({ reviews, reviewSummary, totalReviewCount, primaryColor }: SpotlightReviewsProps) {
  const [showAll, setShowAll] = useState(false);
  const { ref: animRef, isVisible } = useAnimateOnScroll(0.15);
  const { ref: scrollRef, progress } = useScrollProgress();

  if (reviews.length === 0) return null;

  const avgRating = reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
  const displayedReviews = showAll ? reviews : reviews.slice(0, 3);
  const hasMore = reviews.length > 3;
  const accentColor = primaryColor ?? "#3B82F6";
  const parallaxY = (progress - 0.5) * -30;

  return (
    <section
      ref={scrollRef}
      className="relative w-full py-24 md:py-32 px-6 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)",
      }}
    >
      <SpotlightOrbs primaryColor={primaryColor} />
      <NoiseTexture />

      <div
        ref={animRef}
        className="relative z-10 max-w-5xl mx-auto will-change-transform"
        style={{ transform: `translateY(${parallaxY}px)` }}
      >
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/40 mb-12">
          Client Reviews
        </p>

        {/* AI summary — top of section */}
        {reviewSummary && (
          <div
            className={`mb-16 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
              <Sparkles className="h-6 w-6 text-amber-400 shrink-0" />
              What AI Thinks Our Clients Are Saying
            </h3>
            <p className="text-lg md:text-xl text-white/70 leading-relaxed">
              {reviewSummary}
            </p>
          </div>
        )}

        {/* Big rating — staggered entrance */}
        <div className="mb-16">
          <div
            className={`flex items-end gap-4 mb-4 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
            style={{ transitionDelay: reviewSummary ? "200ms" : "0ms" }}
          >
            <span className="text-6xl md:text-7xl font-bold text-white leading-none">
              {avgRating.toFixed(1)}
            </span>
            <div className="pb-2">
              <StarRating rating={Math.round(avgRating)} size="lg" />
              <p className="text-white/40 text-sm mt-1">
                {(totalReviewCount ?? reviews.length).toLocaleString()} review{(totalReviewCount ?? reviews.length) !== 1 ? "s" : ""} on Zillow
              </p>
            </div>
          </div>
        </div>

        {/* Individual reviews — staggered cascade */}
        <div className="space-y-0">
          {displayedReviews.map((review, index) => (
            <div
              key={index}
              className={`py-6 border-t border-white/10 transition-all duration-500 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${500 + index * 150}ms` }}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-white">{review.reviewer_name}</p>
                  {review.reviewer_badge && (
                    <p className="text-xs text-white/40">{review.reviewer_badge}</p>
                  )}
                </div>
                <div className="text-right">
                  <StarRating rating={review.rating} />
                  {review.review_date && (
                    <p className="text-xs text-white/30 mt-1">{formatReviewDate(review.review_date)}</p>
                  )}
                </div>
              </div>
              {review.review_text && (
                <p className="text-white/60 leading-relaxed">
                  {review.review_text}
                </p>
              )}
            </div>
          ))}
        </div>

        {hasMore && !showAll && (
          <div className="flex justify-center mt-8">
            <button
              onClick={() => setShowAll(true)}
              className="flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white/60 hover:text-white hover:border-white/40 hover:scale-105 transition-all duration-300 text-sm"
            >
              <ChevronDown className="h-4 w-4" />
              Show Most Recent {reviews.length} Reviews
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
